import React, { forwardRef, useState, useRef, useEffect, useMemo } from 'react'
import { createPortal } from 'react-dom'
import classNames from 'classnames'
import { getInputBaseClasses, getInputLabelClasses, getInputHelperTextClasses } from '../utils/inputStyles'

export interface AutocompleteOption {
  value: string
  label: string
  disabled?: boolean
}

export interface AutocompleteProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  value?: string
  onChange: (value: string) => void
  options: AutocompleteOption[]
  label?: string
  placeholder?: string
  error?: boolean
  helperText?: string
  disabled?: boolean
  required?: boolean
  name?: string
  loading?: boolean
  noOptionsText?: string
}

const ChevronDownIcon = ({ className }: { className?: string }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    fill="none" 
    viewBox="0 0 24 24" 
    strokeWidth={1.5} 
    stroke="currentColor" 
    className={className}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
  </svg>
)

const CheckIcon = ({ className }: { className?: string }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    fill="none" 
    viewBox="0 0 24 24" 
    strokeWidth={1.5} 
    stroke="currentColor" 
    className={className}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
  </svg>
)

const Autocomplete = forwardRef<HTMLInputElement, AutocompleteProps>(
  ({ 
    value, 
    onChange, 
    options, 
    label, 
    placeholder = 'Selecione uma opção', 
    disabled, 
    className = '', 
    required, 
    error, 
    helperText, 
    id, 
    name, 
    loading = false,
    noOptionsText = 'Nenhuma opção encontrada',
    ...props 
  }, ref) => {
    const [isOpen, setIsOpen] = useState(false)
    const [inputValue, setInputValue] = useState('')
    const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 })
    
    const containerRef = useRef<HTMLDivElement>(null)
    const listRef = useRef<HTMLUListElement>(null)
    const dropdownRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLInputElement>(null)

    // Sync input value with selected option
    useEffect(() => {
      const selectedOption = options.find(opt => opt.value === value)
      if (selectedOption) {
        setInputValue(selectedOption.label)
      } else if (!value) {
        setInputValue('')
      }
      // If value exists but option not found (e.g. data loading), we might want to keep inputValue empty or handle it differently
      // For now assuming options cover the value
    }, [value, options])

    const filteredOptions = useMemo(() => {
      if (!inputValue) return options
      // If the input matches exactly the selected option, show all options (user just opened it)
      // or filter based on input
      // But commonly autocomplete filters as you type.
      // If the input is exactly the label of the selected value, we might want to show all? 
      // Usually, if user types, we filter.
      const selectedOption = options.find(opt => opt.value === value)
      if (selectedOption && selectedOption.label === inputValue) {
        return options
      }

      return options.filter(option => 
        option.label.toLowerCase().includes(inputValue.toLowerCase())
      )
    }, [options, inputValue, value])

    // Close on click outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent | TouchEvent) => {
        if (!isOpen) return
        
        const target = event.target as Node
        const container = containerRef.current
        const dropdown = dropdownRef.current

        if (
          container && container.contains(target) ||
          dropdown && dropdown.contains(target)
        ) {
          return
        }

        setIsOpen(false)
        // Reset input to selected value if closed without selection change
        const selectedOption = options.find(opt => opt.value === value)
        if (selectedOption) {
          setInputValue(selectedOption.label)
        } else {
          setInputValue('')
        }
      }

      const handleScroll = (event: Event) => {
        if (!isOpen) return
        const target = event.target as HTMLElement
        const dropdown = dropdownRef.current
        if (dropdown && dropdown.contains(target)) return
        setIsOpen(false)
        // Reset input on close
        const selectedOption = options.find(opt => opt.value === value)
        if (selectedOption) {
          setInputValue(selectedOption.label)
        } else {
          setInputValue('')
        }
      }

      if (isOpen) {
        window.addEventListener('mousedown', handleClickOutside)
        window.addEventListener('touchstart', handleClickOutside)
        window.addEventListener('scroll', handleScroll, true)
        window.addEventListener('resize', handleScroll)
      }

      return () => {
        window.removeEventListener('mousedown', handleClickOutside)
        window.removeEventListener('touchstart', handleClickOutside)
        window.removeEventListener('scroll', handleScroll, true)
        window.removeEventListener('resize', handleScroll)
      }
    }, [isOpen, value, options])

    // Calculate position
    useEffect(() => {
      if (isOpen && containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect()
        setCoords({
          top: rect.bottom,
          left: rect.left,
          width: rect.width
        })
      }
    }, [isOpen])

    const handleSelect = (option: AutocompleteOption) => {
      onChange(option.value)
      setInputValue(option.label)
      setIsOpen(false)
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value)
      setIsOpen(true)
      
      // Optional: Clear selection if input doesn't match
      if (value) {
         // If we want to allow clearing by typing:
         // onChange('')
      }
    }

    const handleInputClick = () => {
      if (!disabled) {
        setIsOpen(true)
      }
    }
    
    // Allow ref forwarding
    React.useImperativeHandle(ref, () => inputRef.current as HTMLInputElement)

    const baseInputClasses = getInputBaseClasses(error, disabled, false, classNames('bg-white flex items-center justify-between cursor-text relative pr-10', className))

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (disabled) return

      if (e.key === 'Escape') {
        setIsOpen(false)
        const selectedOption = options.find(opt => opt.value === value)
        if (selectedOption) setInputValue(selectedOption.label)
        else setInputValue('')
        inputRef.current?.blur()
      } else if (e.key === 'ArrowDown' && !isOpen) {
        e.preventDefault()
        setIsOpen(true)
      } else if (e.key === 'Tab') {
          setIsOpen(false)
      }
    }

    const dropdownContent = (
      <div 
        ref={dropdownRef}
        className="fixed z-[9999] mt-1 max-h-60 overflow-auto overscroll-contain rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm animate-in fade-in zoom-in-95 duration-100"
        style={{
          top: coords.top,
          left: coords.left,
          width: coords.width
        }}
      >
        <ul
          ref={listRef}
          role="listbox"
          className="outline-none"
        >
          {loading ? (
            <li className="relative cursor-default select-none py-2 pl-3 pr-9 text-gray-500 italic">
              Carregando...
            </li>
          ) : filteredOptions.length === 0 ? (
            <li className="relative cursor-default select-none py-2 pl-3 pr-9 text-gray-500 italic">
              {noOptionsText}
            </li>
          ) : (
            filteredOptions.map((option) => {
              const isSelected = option.value === value
              const isDisabled = option.disabled

              return (
                <li
                  key={option.value}
                  className={classNames(
                    'relative cursor-pointer select-none py-2.5 pl-3 pr-9 transition-colors',
                    {
                      'bg-blue-50 text-blue-900': isSelected,
                      'text-gray-900 hover:bg-gray-100': !isSelected && !isDisabled,
                      'text-gray-400 cursor-not-allowed opacity-60': isDisabled,
                    }
                  )}
                  role="option"
                  aria-selected={isSelected}
                  aria-disabled={isDisabled}
                  onMouseDown={(e) => {
                    // Use onMouseDown to prevent input blur before click is processed
                    e.preventDefault() 
                  }}
                  onClick={(e) => {
                    e.stopPropagation()
                    if (!isDisabled) handleSelect(option)
                  }}
                >
                  <div className="flex items-center">
                    <span className={classNames('block truncate', { 'font-semibold': isSelected, 'font-normal': !isSelected })}>
                      {option.label}
                    </span>
                  </div>

                  {isSelected && (
                    <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-blue-600">
                      <CheckIcon className="h-5 w-5" />
                    </span>
                  )}
                </li>
              )
            })
          )}
        </ul>
      </div>
    )

    return (
      <div className="w-full relative" ref={containerRef}>
        {label && (
          <label 
            htmlFor={id} 
            className={getInputLabelClasses()}
            onClick={() => {
                if (!disabled) {
                    inputRef.current?.focus()
                    setIsOpen(true)
                }
            }}
          >
            {label} {required && <span className="text-red-500">*</span>}
          </label>
        )}
        
        <div className="relative">
            <input
            ref={inputRef}
            type="text"
            id={id}
            name={name}
            className={baseInputClasses}
            value={inputValue}
            onChange={handleInputChange}
            onClick={handleInputClick}
            onFocus={() => !disabled && setIsOpen(true)}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            placeholder={placeholder}
            autoComplete="off"
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            aria-invalid={error || undefined}
            {...props}
            />
            <button
                type="button"
                className="absolute inset-y-0 right-0 flex items-center pr-3 cursor-pointer outline-none"
                onClick={() => {
                    if (!disabled) {
                        if (isOpen) {
                            setIsOpen(false)
                        } else {
                            inputRef.current?.focus()
                            setIsOpen(true)
                        }
                    }
                }}
                tabIndex={-1}
            >
                <ChevronDownIcon 
                className={classNames(
                    "h-5 w-5 text-gray-400 transition-transform duration-200",
                    { "transform rotate-180": isOpen }
                )} 
                />
            </button>
        </div>

        {isOpen && createPortal(dropdownContent, document.body)}

        {helperText && (
          <p className={getInputHelperTextClasses(error)}>
            {helperText}
          </p>
        )}
      </div>
    )
  }
)

Autocomplete.displayName = 'Autocomplete'

export default Autocomplete
